// /src/pages/CreateBranch.jsx
import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";
import { useGetAllCompaniesQuery } from "../toolkitServices/companyApi";
import { useAddBranchMutation } from "../toolkitServices/branchApi";
import { indianStates } from "../utils/helpers";

/* ---------- Shared UI tokens (align with CreateCategory) ---------- */
const baseInput =
  "w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none";
const okRing =
  "border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]";
const errRing =
  "border-red-400 focus:ring-4 focus:ring-red-200 focus:border-red-500";

const CreateBranch = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Companies for dropdown
  const {
    data: companiesResp,
    isLoading: isCompaniesLoading,
    error: companiesError,
  } = useGetAllCompaniesQuery({ page: 1, limit: 50, search: "" });

  const companies =
    companiesResp?.data || companiesResp?.items || companiesResp?.rows || [];

  // RTK mutation
  const [
    addBranch,
    { isLoading: isSaving, error: saveError, isSuccess, data },
  ] = useAddBranchMutation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    trigger,
    reset,
    watch,
    setFocus, // RHF focus API
  } = useForm({
    defaultValues: {
      branchName: "",
      company: "",
      contact: "",
      email: "",
      gstNumber: "",
      addressLine1: "",
      city: "",
      pincode: "",
      state: "Maharashtra",
      country: "india",
    },
    mode: "onBlur",
  });

  // Success handling
  useEffect(() => {
    if (isSuccess && (data?.success ?? true)) {
      toast.success("Branch added!");
      reset({
        branchName: "",
        company: "",
        contact: "",
        email: "",
        gstNumber: "",
        addressLine1: "",
        city: "",
        pincode: "",
        state: "Maharashtra",
        country: "india",
      });
      setStep(1);
      requestAnimationFrame(() =>
        setFocus("branchName", { shouldSelect: true }),
      );
    }
  }, [isSuccess, data, reset, setFocus]);

  // Error handling
  useEffect(() => {
    if (saveError) {
      const msg =
        saveError?.data?.msg ||
        saveError?.data?.message ||
        saveError?.error ||
        "Error adding branch!";
      toast.error(msg);
    }
  }, [saveError]);

  // Stepper helpers + shortcuts
  const nextStep = useCallback(async () => {
    const valid = await trigger();
    if (valid) setStep((s) => (s < 2 ? s + 1 : s));
  }, [trigger]);

  const prevStep = useCallback(() => {
    setStep((s) => (s > 1 ? s - 1 : s));
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const isSave = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s";
      const isBack = (e.ctrlKey || e.metaKey) && e.key === "Backspace";
      const isPrev = e.altKey && e.key === "ArrowLeft";

      if (isSave) {
        e.preventDefault();
        if (step === 2) {
          handleSubmit(onSubmit)();
        } else {
          nextStep();
        }
      }

      if (isBack) {
        e.preventDefault();
        navigate(-1);
      }

      if (isPrev) {
        e.preventDefault();
        prevStep();
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [step, handleSubmit, navigate, nextStep, prevStep]);

  // Auto-focus per step
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      if (step === 1) setFocus("branchName", { shouldSelect: true });
      if (step === 2) setFocus("addressLine1", { shouldSelect: true });
    });
    return () => cancelAnimationFrame(id);
  }, [step, setFocus]);

  // Sidebar title
  const branchName = watch("branchName");

  // Submit
  const onSubmit = async (form) => {
    const payload = {
      branchName: (form.branchName || "").trim(),
      company: form.company,
      contact: (form.contact || "").trim(),
      email: (form.email || "").trim(),
      gstNumber: (form.gstNumber || "").trim().toUpperCase(),
      addressLine1: (form.addressLine1 || "").trim(),
      city: (form.city || "").trim(),
      pincode: (form.pincode || "").trim(),
      state: form.state,
      country: form.country,
    };

    try {
      await addBranch(payload).unwrap();
      // success flow handled in isSuccess effect
    } catch {
      // toast handled in error effect
    }
  };

  const loading = isSaving || isSubmitting;

  return (
    <div className="px-4 py-4">
      {/* Compact Header (consistent with CreateCategory) */}
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-2 py-2 flex items-center gap-3">
          <Link
            to={"/organisation/branch-master-list"}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 transition"
            title="Back to Branch List"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h1 className="text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight">
                Create Branch
              </h1>
              <span className="text-xs text-gray-500 mr-2">
                Step {step} of 2
              </span>
            </div>
            <p className="text-xs text-gray-500">
              Tips: <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">S</kbd> to
              save/next,{" "}
              <kbd className="px-1 bg-gray-100 rounded border ml-2">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">Back Spc</kbd> go
              back,{" "}
              <kbd className="px-1 bg-gray-100 rounded border ml-2">Alt</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">←</kbd> prev step
            </p>
          </div>
        </div>
      </div>

      {/* Card — compact; BORDER-ONLY validation; loading overlay */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        className="max-w-5xl mx-auto bg-white shadow-md rounded-xl mt-4 border border-gray-200 relative"
        onAnimationComplete={() =>
          requestAnimationFrame(() =>
            setFocus(step === 1 ? "branchName" : "addressLine1"),
          )
        }
      >
        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-xl">
            <div className="animate-spin h-7 w-7 border-2 border-[#9481ce] border-t-transparent rounded-full" />
          </div>
        )}

        <form
          className="p-5"
          onSubmit={(e) => e.preventDefault()} // manual control (Ctrl+S / button)
        >
          {/* STEP 1 */}
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Branch Name */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Branch Name <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("branchName", {
                    required: "Branch name is required",
                  })}
                  aria-invalid={!!errors.branchName}
                  placeholder="Enter branch name"
                  className={`${baseInput} ${
                    errors.branchName ? errRing : okRing
                  }`}
                />
                {errors.branchName?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.branchName.message}
                  </p>
                )}
              </div>

              {/* Company */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Company <span className="text-red-500">*</span>
                </label>
                <select
                  {...register("company", { required: "Company is required" })}
                  aria-invalid={!!errors.company}
                  disabled={isCompaniesLoading || companiesError}
                  className={`${baseInput} ${
                    errors.company ? errRing : okRing
                  }`}
                >
                  <option value="">
                    {isCompaniesLoading
                      ? "Loading companies…"
                      : companiesError
                        ? "Failed to load companies"
                        : "Select a company"}
                  </option>
                  {companies.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.companyName}
                    </option>
                  ))}
                </select>
                {errors.company?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.company.message}
                  </p>
                )}
              </div>

              {/* Contact */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Contact
                </label>
                <input
                  {...register("contact", {
                    validate: (value) =>
                      value === "" ||
                      /^\d{10}$/.test(value) ||
                      "Contact must be exactly 10 digits",
                  })}
                  aria-invalid={!!errors.contact}
                  placeholder="Enter contact"
                  className={`${baseInput} ${
                    errors.contact ? errRing : okRing
                  }`}
                />
                {errors.contact?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.contact.message}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  {...register("email", {
                    validate: (value) =>
                      !value ||
                      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ||
                      "Invalid email address",
                  })}
                  aria-invalid={!!errors.email}
                  placeholder="Enter email"
                  className={`${baseInput} ${errors.email ? errRing : okRing}`}
                />
                {errors.email?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* GSTIN */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  GSTIN Number
                </label>
                <input
                  {...register("gstNumber", {
                    validate: (value) =>
                      value === "" ||
                      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
                        (value || "").toUpperCase(),
                      ) ||
                      "Invalid GSTIN format (e.g. 27ABCDE1234F1Z5)",
                  })}
                  aria-invalid={!!errors.gstNumber}
                  placeholder="Enter GSTIN Number"
                  className={`${baseInput} ${
                    errors.gstNumber ? errRing : okRing
                  }`}
                />
                {errors.gstNumber?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.gstNumber.message}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Address Line 1 (span 3) */}
              <div className="md:col-span-3">
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Address Line 1
                </label>
                <input
                  {...register("addressLine1")}
                  placeholder="Enter street / area"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("city", { required: "City is required" })}
                  aria-invalid={!!errors.city}
                  placeholder="Enter city"
                  className={`${baseInput} ${errors.city ? errRing : okRing}`}
                />
                {errors.city?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.city.message}
                  </p>
                )}
              </div>

              {/* Pincode */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Pincode <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("pincode", {
                    required: "Pincode is required",
                    validate: (value) =>
                      value === "" ||
                      /^\d{6}$/.test(value) ||
                      "Pincode must be a 6-digit number",
                  })}
                  aria-invalid={!!errors.pincode}
                  placeholder="Enter pincode"
                  className={`${baseInput} ${errors.pincode ? errRing : okRing}`}
                />
                {errors.pincode?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.pincode.message}
                  </p>
                )}
              </div>

              {/* State */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  {...register("state", { required: "State is required" })}
                  aria-invalid={!!errors.state}
                  className={`${baseInput} ${errors.state ? errRing : okRing}`}
                  
                >
                  <option value="">Select State</option>

                  {indianStates.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>

              {/* Country */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Country <span className="text-red-500">*</span>
                </label>
                <select
                  {...register("country", { required: "Country is required" })}
                  aria-invalid={!!errors.country}
                  className={`${baseInput} ${errors.country ? errRing : okRing}`}
                >
                  <option value={"india"}>India</option>
                </select>
              </div>
            </div>
          )}

          {/* Footer actions */}
          <div className="flex flex-col sm:flex-row gap-2 pt-5">
            <motion.button
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              type="button"
              onClick={prevStep}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-60"
              disabled={step === 1 || loading}
              title="Previous step"
            >
              Previous
            </motion.button>

            <motion.button
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (step === 2) handleSubmit(onSubmit)();
                else nextStep();
              }}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium shadow-sm bg-[#9481ce] hover:bg-[#7d6ec0] text-white disabled:opacity-60"
              disabled={loading}
              title={step === 2 ? "Submit (Ctrl+S)" : "Next (Ctrl+S)"}
            >
              {loading ? "Saving..." : step === 2 ? "Submit (Ctrl+S)" : "Next"}
            </motion.button>

            <Link
              to="/organisation/branch-master-list"
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
              title="Cancel and go back"
            >
              Cancel
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CreateBranch;
